import { Plan, Space } from "../../models";
import { PricingTiers, pricingTiers } from "./prices";

function calculateTieredPrice(tiers: PricingTiers, included: number, amount: number) {
  let chargeable = Math.max(0, amount - included);
  return tiers.reduce((acc, tier) => {
    const units = Math.min(chargeable, tier.max);
    chargeable = Math.max(0, chargeable - units);
    return acc + units * tier.costPerUnit;
  }, 0);
}

export type PriceEstimation = ReturnType<typeof estimatePlanCosts>;

export default function estimatePlanCosts(
  space: Space,
  newPlan: Pick<Plan, "platformFee" | "usage" | "costPerEditor" | "interval">
) {
  const currentPlan = space?.plan;
  const currentUsage = currentPlan?.usage;
  const newUsage = newPlan.usage;
  const interval = newPlan.interval;

  const editorsCount =
    newUsage?.editors.quota ?? currentUsage?.editors.quota ?? space.counts.editors;
  const storageCount = Math.max(
    newUsage?.storage.quota ?? currentUsage?.storage.quota ?? 0,
    newUsage?.storage.included,
    0
  );
  const consumerCount = Math.max(
    newUsage?.consumers.quota ?? currentUsage?.consumers.quota ?? 0,
    newUsage?.consumers.included,
    0
  );
  const dlRequestCount = Math.max(
    newUsage?.dlRequests.quota ?? currentUsage?.dlRequests.quota ?? 0,
    newUsage?.dlRequests.included,
    0
  );
  const dlBandwidthCount = Math.max(
    newUsage?.dlBandwidth.quota ?? currentUsage?.dlBandwidth.quota ?? 0,
    newUsage?.dlBandwidth.included,
    0
  );

  const consumerCost = calculateTieredPrice(
    pricingTiers.consumers,
    newUsage?.consumers.included ?? 0,
    consumerCount
  );
  const storageCost = calculateTieredPrice(
    pricingTiers.storage,
    newUsage?.storage.included ?? 0,
    storageCount
  );
  const dlRequestCost = calculateTieredPrice(
    pricingTiers.dlRequests,
    newUsage?.dlRequests.included ?? 0,
    dlRequestCount
  );
  const dlBandwidthCost = calculateTieredPrice(
    pricingTiers.dlBandwidth,
    newUsage?.dlBandwidth.included ?? 0,
    dlBandwidthCount
  );

  const monthlyDivisor = interval === "year" ? 12 : 1;
  const monthlyPlatformFee = newPlan.platformFee / monthlyDivisor;
  const monthlyPricePerEditor = newPlan.costPerEditor / monthlyDivisor;
  const editorCost = (newPlan.costPerEditor * editorsCount) / monthlyDivisor;

  const totalMonthly =
    monthlyPlatformFee + editorCost + consumerCost + storageCost + dlRequestCost + dlBandwidthCost;

  return {
    total: totalMonthly,
    platformFee: {
      total: monthlyPlatformFee,
    },
    editors: {
      count: editorsCount,
      perEditor: monthlyPricePerEditor,
      total: editorCost,
    },
    consumers: {
      count: consumerCount,
      total: consumerCost,
    },
    storage: {
      count: storageCount,
      total: storageCost,
    },
    dlRequests: {
      count: dlRequestCount,
      total: dlRequestCost,
    },
    dlBandwidth: {
      count: dlBandwidthCount,
      total: dlBandwidthCost,
    },
  };
}
